/* Helpers */
import { request } from '../helpers/mainHelpers.js';

const initState = {
  isVisible: false,
  product: {
    pvName: '',
    imageSrc: '',
    altText: '',
  },
  compatibleAccessories: {},
  compatibleAccessoriesPrices: {},
};

export default {
  state: { ...initState },
  getters: {
    getInterstitialIsVisible(state) {
      return state.isVisible;
    },
    getInterstitialProductPvName: (state) => state.product?.pvName,
    getInterstitialProductImage(state) {
      return state.product.imageSrc;
    },
    getInterstitialProductAltText(state) {
      return state.product.altText;
    },
    getInterstitialAccessories(state) {
      return (productId) => state.compatibleAccessories[productId];
    },
    getHasInterstitialAccessories(state, getters) {
      const productId = getters.getProductId;
      const items = getters.getInterstitialAccessories(productId) || [];
      return !!items.length;
    },
    getInterstitialAccessoryPrices(state) {
      return state.compatibleAccessoriesPrices;
    },
    getInterstitialAccessoryPriceByUrl(state, getters) {
      return (priceUrl) => getters.getInterstitialAccessoryPrices[priceUrl];
    },
  },
  mutations: {
    resetInterstitial(state) {
      state.isVisible = initState.isVisible;
      state.product = { ...initState.product };
    },
    setInterstitialIsVisible(state, payload) {
      state.isVisible = payload;
    },
    setInterstitialProductDetails(state, payload) {
      state.product = payload;
    },
    setInterstitialAccessory(state, payload) {
      const { productId, data } = payload;
      const accessories = state.compatibleAccessories[productId];

      state.compatibleAccessories[productId] = accessories.map((item) => {
        if (item.partNumber === data.partNumber) {
          return data;
        }
        return item;
      });
    },
    setInterstitialAccessories(state, payload) {
      const { data, productId } = payload;
      state.compatibleAccessories = { ...state.compatibleAccessories, [productId]: data };
    },
    setInterstitialAccessoryPrice(state, payload) {
      const { data, priceUrl } = payload;
      state.compatibleAccessoriesPrices = {
        ...state.compatibleAccessoriesPrices,
        [priceUrl]: data,
      };
    },
  },
  actions: {
    resetInterstitial({ commit }) {
      commit('resetInterstitial');
    },
    setInterstitialIsVisible({ commit }, payload) {
      commit('setInterstitialIsVisible', payload);
    },
    setInterstitialProductDetails({ commit }, payload) {
      commit('setInterstitialProductDetails', payload);
    },
    async callInterstitialAccessories({ commit, getters, rootGetters }) {
      const { getCustomerGroups } = rootGetters;
      const {
        getBuyGarminEndpoint, getLocale, getCountryCode,
        getProductId, getProductPageApi,
      } = getters;

      const url = `${getProductPageApi}/${getProductId}/compatibleProducts`;
      let accessories = getters.getInterstitialAccessories(getProductId);

      if (!accessories) {
        accessories = await request({
          url,
        });

        commit('setInterstitialAccessories', {
          productId: getProductId,
          data: accessories,
        });
      }

      // Continue to get the prices
      accessories.forEach(async (item) => {
        const queryParams = new URLSearchParams();
        queryParams.append('locale', getLocale);
        /**
         * If there are multiple customerGroups, they will be divided by a |
         * new URLSearchParams() will convert | to %7C which the pricing APIs expect when there are multiple customer groups
         */
        queryParams.append('customerGroup', getCustomerGroups);

        const locationHref = window?.location?.href;
        if (locationHref.includes('cdncache=false')) queryParams.append('cdncache', false);
        // Make the API call for the pricing proxy
        const priceUrl = `${getBuyGarminEndpoint}/pricing-proxy-services/countries/${getCountryCode}/skus/${item.partNumber}/price?${queryParams}`;

        // If accessory price has already been called and cached, return null
        // returning null indicates the price was not called
        const cachedPrice = getters.getInterstitialAccessoryPriceByUrl(priceUrl);

        // if the price call is already cached, return that, else call to get price
        const itemPrice = cachedPrice || await request({
          url: priceUrl,
        }).catch(() => {
          console.error('Error calling pricing proxy service');
        });

        const newPrice = {
          data: itemPrice || {},
          priceUrl,
        };

        // Add the price to the accessory
        item.priceObj = itemPrice || {};

        // Commit the accessory again with the price updated
        commit('setInterstitialAccessory', {
          productId: getProductId,
          data: item,
        });

        // Cache accessory price
        commit('setInterstitialAccessoryPrice', newPrice);
      });
    },
  },
};
